import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TypeAheadSelect } from '@/components/ui/type-ahead-select';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Trash2 } from 'lucide-react';
import { questionApi } from '@/lib/api';
import { useToast } from '@/components/ui/use-toast';
import { KatexPreviewPanel } from '@/components/ui/katex-preview';
import VirtualKeyboard from '@/components/VirtualKeyboard';

interface Option {
  text: string;
  isCorrect: boolean;
}

interface Question {
  id: string;
  questionText: string;
  type: string;
  options: string[];
  correctAnswer?: string; // For FILL_IN_THE_BLANK_NUMERIC questions
  correctAnswers: string[]; // For MCQ questions
  explanation?: string;
  subject: string;
  topic: string;
  subtopic?: string;
  difficultyLevel: string;
  examTypes?: string[]; // NEW: Multiple exam types support
  tags?: string[]; // NEW: Tag-based categorization
  tenantCode?: string;
}

interface EditQuestionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  question: Question | null;
}

// Supported question types only
const QUESTION_TYPES = [
  { value: 'MCQ_SINGLE_CORRECT', label: 'MCQ Single Correct' },
  { value: 'MCQ_MULTIPLE_CORRECT', label: 'MCQ Multiple Correct' },
  { value: 'TRUE_FALSE', label: 'True/False' },
  { value: 'FILL_IN_THE_BLANK_NUMERIC', label: 'Fill in the Blank (Numeric)' }
];

const DIFFICULTY_LEVELS = [
  { value: 'EASY', label: 'Easy' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HARD', label: 'Hard' }
];

export const EditQuestionDialog: React.FC<EditQuestionDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
  question
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    questionText: '',
    type: 'MCQ_SINGLE_CORRECT',
    options: [
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false }
    ] as Option[],
    numericalAnswer: '',
    explanation: '',
    subject: '',
    topic: '',
    subtopic: '',
    difficultyLevel: 'MEDIUM',
    examTypes: [] as string[], // NEW: Multiple exam types support
    tags: [] as string[], // NEW: Tag-based categorization
    tenantCode: ''
  });

  // Type-ahead options
  const [subjectOptions, setSubjectOptions] = useState<Array<{value: string, label: string}>>([]);
  const [topicOptions, setTopicOptions] = useState<Array<{value: string, label: string}>>([]);
  const [subtopicOptions, setSubtopicOptions] = useState<Array<{value: string, label: string}>>([]);
  const [examTypeOptions, setExamTypeOptions] = useState<Array<{value: string, label: string}>>([]);

  // Loading states
  const [subjectLoading, setSubjectLoading] = useState(false);
  const [topicLoading, setTopicLoading] = useState(false);
  const [subtopicLoading, setSubtopicLoading] = useState(false);
  const [examTypeLoading, setExamTypeLoading] = useState(false);

  // Tag management state
  const [newTag, setNewTag] = useState('');

  // Load question data when dialog opens
  useEffect(() => {
    if (isOpen && question) {
      loadQuestionData();
      loadInitialData();
    }
  }, [isOpen, question]);

  const loadQuestionData = () => {
    if (!question) return;

    // Convert question data to form format
    const options: Option[] = [];

    if (question.type === 'FILL_IN_THE_BLANK_NUMERIC') {
      setFormData(prev => ({
        ...prev,
        questionText: question.questionText,
        type: question.type,
        options: [],
        numericalAnswer: question.correctAnswer || question.correctAnswers?.[0] || '',
        explanation: question.explanation || '',
        subject: question.subject,
        topic: question.topic,
        subtopic: question.subtopic || '',
        difficultyLevel: question.difficultyLevel,
        examTypes: question.examTypes || [], // NEW: Multiple exam types support
        tags: question.tags || [], // NEW: Tag-based categorization
        tenantCode: question.tenantCode || ''
      }));
    } else {
      // For MCQ and TRUE_FALSE
      question.options.forEach(optionText => {
        options.push({
          text: optionText,
          isCorrect: question.correctAnswers.includes(optionText)
        });
      });

      setFormData(prev => ({
        ...prev,
        questionText: question.questionText,
        type: question.type,
        options,
        numericalAnswer: '',
        explanation: question.explanation || '',
        subject: question.subject,
        topic: question.topic,
        subtopic: question.subtopic || '',
        difficultyLevel: question.difficultyLevel,
        examTypes: question.examTypes || [], // NEW: Multiple exam types support
        tags: question.tags || [], // NEW: Tag-based categorization
        tenantCode: question.tenantCode || ''
      }));
    }
  };

  const loadInitialData = async () => {
    try {
      // Load initial subjects and exam types
      await Promise.all([
        searchSubjects(''),
        searchExamTypes('')
      ]);

      // Load topics and subtopics if subject/topic are set
      if (question?.subject) {
        await searchTopics('');
        if (question?.topic) {
          await searchSubtopics('');
        }
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  // Search functions (same as AddQuestionDialog)
  const searchSubjects = async (search: string) => {
    try {
      setSubjectLoading(true);
      const results = await questionApi.searchSubjects(search);
      setSubjectOptions(results.map((item: string) => ({ value: item, label: item })));
    } catch (error) {
      console.error('Error searching subjects:', error);
    } finally {
      setSubjectLoading(false);
    }
  };

  const searchTopics = async (search: string) => {
    try {
      setTopicLoading(true);
      const results = await questionApi.searchTopics(formData.subject, search);
      setTopicOptions(results.map((item: string) => ({ value: item, label: item })));
    } catch (error) {
      console.error('Error searching topics:', error);
    } finally {
      setTopicLoading(false);
    }
  };

  const searchSubtopics = async (search: string) => {
    try {
      setSubtopicLoading(true);
      const results = await questionApi.searchSubtopics(formData.topic, search);
      setSubtopicOptions(results.map((item: string) => ({ value: item, label: item })));
    } catch (error) {
      console.error('Error searching subtopics:', error);
    } finally {
      setSubtopicLoading(false);
    }
  };

  const searchExamTypes = async (_search: string) => {
    try {
      setExamTypeLoading(true);
      const results = await questionApi.getExamTypes();
      setExamTypeOptions(results.map((item: string) => ({ value: item, label: item })));
    } catch (error) {
      console.error('Error searching exam types:', error);
    } finally {
      setExamTypeLoading(false);
    }
  };

  // Tag management functions
  const addTag = () => {
    const tag = newTag.trim().toLowerCase();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Handle subject change - reset topic and subtopic
  const handleSubjectChange = (subject: string) => {
    setFormData(prev => ({
      ...prev,
      subject,
      topic: '',
      subtopic: ''
    }));
    setTopicOptions([]);
    setSubtopicOptions([]);
    if (subject) {
      searchTopics('');
    }
  };

  // Handle topic change - reset subtopic
  const handleTopicChange = (topic: string) => {
    setFormData(prev => ({
      ...prev,
      topic,
      subtopic: ''
    }));
    setSubtopicOptions([]);
    if (topic) {
      searchSubtopics('');
    }
  };

  // Handle option changes (same as AddQuestionDialog)
  const updateOption = (index: number, text: string) => {
    const newOptions = [...formData.options];
    newOptions[index].text = text;
    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  const toggleOptionCorrect = (index: number) => {
    const newOptions = [...formData.options];

    if (formData.type === 'MCQ_SINGLE_CORRECT') {
      newOptions.forEach((option, i) => {
        option.isCorrect = i === index;
      });
    } else {
      newOptions[index].isCorrect = !newOptions[index].isCorrect;
    }

    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  const addOption = () => {
    if (formData.options.length < 6) {
      setFormData(prev => ({
        ...prev,
        options: [...prev.options, { text: '', isCorrect: false }]
      }));
    }
  };

  const removeOption = (index: number) => {
    if (formData.options.length > 2) {
      const newOptions = formData.options.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, options: newOptions }));
    }
  };

  // Handle question type change
  const handleQuestionTypeChange = (type: string) => {
    setFormData(prev => {
      const newOptions = [...prev.options];

      if (type === 'TRUE_FALSE') {
        return {
          ...prev,
          type,
          options: [
            { text: 'True', isCorrect: false },
            { text: 'False', isCorrect: false }
          ],
          numericalAnswer: '' // Clear numerical answer
        };
      } else if (type === 'FILL_IN_THE_BLANK_NUMERIC') {
        return {
          ...prev,
          type,
          options: [], // Clear options for numeric questions
          numericalAnswer: prev.numericalAnswer || '' // Keep existing numerical answer
        };
      } else if (type === 'MCQ_SINGLE_CORRECT') {
        let hasCorrect = false;
        newOptions.forEach(option => {
          if (option.isCorrect && !hasCorrect) {
            hasCorrect = true;
          } else {
            option.isCorrect = false;
          }
        });
        return {
          ...prev,
          type,
          options: newOptions,
          numericalAnswer: '' // Clear numerical answer
        };
      }

      return {
        ...prev,
        type,
        options: newOptions,
        numericalAnswer: type === 'FILL_IN_THE_BLANK_NUMERIC' ? prev.numericalAnswer : ''
      };
    });
  };

  // Validation (same as AddQuestionDialog)
  const validateForm = () => {
    if (!formData.questionText.trim()) {
      toast({ title: "Error", description: "Question text is required", variant: "destructive" });
      return false;
    }

    if (!formData.subject.trim()) {
      toast({ title: "Error", description: "Subject is required", variant: "destructive" });
      return false;
    }

    if (!formData.topic.trim()) {
      toast({ title: "Error", description: "Topic is required", variant: "destructive" });
      return false;
    }

    if (formData.type.startsWith('MCQ') || formData.type === 'TRUE_FALSE') {
      const hasCorrectAnswer = formData.options.some(option => option.isCorrect);
      if (!hasCorrectAnswer) {
        toast({ title: "Error", description: "At least one correct answer must be selected", variant: "destructive" });
        return false;
      }

      const hasEmptyOption = formData.options.some(option => !option.text.trim());
      if (hasEmptyOption) {
        toast({ title: "Error", description: "All options must have text", variant: "destructive" });
        return false;
      }
    }

    if ((formData.type === 'NUMERICAL' || formData.type === 'NUMERIC' || formData.type === 'FILL_IN_THE_BLANK_NUMERIC') && !formData.numericalAnswer.trim()) {
      toast({ title: "Error", description: "Answer is required", variant: "destructive" });
      return false;
    }

    return true;
  };

  // Submit form
  const handleSubmit = async () => {
    if (!validateForm() || !question) return;

    try {
      setLoading(true);

      const questionData = {
        questionText: formData.questionText,
        type: formData.type,
        options: (formData.type === 'NUMERICAL' || formData.type === 'NUMERIC' || formData.type === 'FILL_IN_THE_BLANK_NUMERIC') ? [] : formData.options.map(opt => opt.text),
        correctAnswers: (formData.type === 'NUMERICAL' || formData.type === 'NUMERIC' || formData.type === 'FILL_IN_THE_BLANK_NUMERIC')
          ? [formData.numericalAnswer]
          : formData.options.filter(opt => opt.isCorrect).map(opt => opt.text),
        explanation: formData.explanation || null,
        subject: formData.subject,
        topic: formData.topic,
        subtopic: formData.subtopic || null,
        difficultyLevel: formData.difficultyLevel,
        examTypes: formData.examTypes, // NEW: Multiple exam types support
        tags: formData.tags, // NEW: Tag-based categorization
        tenantCode: formData.tenantCode || null
      };

      await questionApi.update(question.id, questionData);

      toast({
        title: "Success",
        description: "Question updated successfully"
      });

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error updating question:', error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to update question",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (!question) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Question</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form Section */}
          <div className="space-y-6">
          {/* Question Text */}
          <div className="space-y-2">
            <Label htmlFor="questionText">Question Text *</Label>
            <Textarea
              id="questionText"
              value={formData.questionText}
              onChange={(e) => setFormData(prev => ({ ...prev, questionText: e.target.value }))}
              placeholder="Enter the question text (LaTeX supported)..."
              rows={3}
            />
          </div>

          {/* Question Type */}
          <div className="space-y-2">
            <Label>Question Type *</Label>
            <Select value={formData.type} onValueChange={handleQuestionTypeChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {QUESTION_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Options for MCQ and True/False */}
          {(formData.type.startsWith('MCQ') || formData.type === 'TRUE_FALSE') && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Answer Options *</Label>
                <div className="text-xs text-gray-500">
                  Debug: Rendering MCQ options for type "{formData.type}"
                </div>
                {formData.type.startsWith('MCQ') && formData.options.length < 6 && (
                  <Button type="button" variant="outline" size="sm" onClick={addOption}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Option
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                {formData.options.map((option, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Checkbox
                      checked={option.isCorrect}
                      onCheckedChange={() => toggleOptionCorrect(index)}
                    />
                    <Input
                      value={option.text}
                      onChange={(e) => updateOption(index, e.target.value)}
                      placeholder={`Option ${String.fromCharCode(65 + index)}`}
                      className="flex-1"
                    />
                    {formData.type.startsWith('MCQ') && formData.options.length > 2 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeOption(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Numerical Answer */}
          {(formData.type === 'NUMERICAL' || formData.type === 'FILL_IN_THE_BLANK_NUMERIC') && (
            <div className="space-y-2">
              <Label htmlFor="numericalAnswer">Numerical Answer *</Label>
              <VirtualKeyboard
                value={formData.numericalAnswer}
                onChange={(value) => setFormData(prev => ({ ...prev, numericalAnswer: value }))}
                onClear={() => setFormData(prev => ({ ...prev, numericalAnswer: '' }))}
                placeholder="Enter the numerical answer"
                className="w-full"
              />
              <div className="text-xs text-gray-500">
                Debug: formData.type = "{formData.type}"
              </div>
            </div>
          )}

          {/* Fill in the Blank Answer - Legacy support */}
          {formData.type === 'FILL_IN_THE_BLANK' && (
            <div className="space-y-2">
              <Label htmlFor="fillInAnswer">Correct Answer *</Label>
              <Input
                id="fillInAnswer"
                type="text"
                value={formData.numericalAnswer}
                onChange={(e) => setFormData(prev => ({ ...prev, numericalAnswer: e.target.value }))}
                placeholder="Enter the correct answer for the blank"
              />
            </div>
          )}

          {/* Subject, Topic, Subtopic */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Subject *</Label>
              <TypeAheadSelect
                value={formData.subject}
                onValueChange={handleSubjectChange}
                placeholder="Select or add subject..."
                options={subjectOptions}
                onSearch={searchSubjects}
                loading={subjectLoading}
                allowCreate={true}
                onCreateNew={(value) => handleSubjectChange(value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Topic *</Label>
              <TypeAheadSelect
                value={formData.topic}
                onValueChange={handleTopicChange}
                placeholder="Select or add topic..."
                options={topicOptions}
                onSearch={searchTopics}
                loading={topicLoading}
                allowCreate={true}
                onCreateNew={(value) => handleTopicChange(value)}
                disabled={!formData.subject}
              />
            </div>

            <div className="space-y-2">
              <Label>Subtopic</Label>
              <TypeAheadSelect
                value={formData.subtopic}
                onValueChange={(value) => setFormData(prev => ({ ...prev, subtopic: value }))}
                placeholder="Select or add subtopic..."
                options={subtopicOptions}
                onSearch={searchSubtopics}
                loading={subtopicLoading}
                allowCreate={true}
                onCreateNew={(value) => setFormData(prev => ({ ...prev, subtopic: value }))}
                disabled={!formData.topic}
              />
            </div>
          </div>

          {/* Difficulty and Kit */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Difficulty Level *</Label>
              <Select value={formData.difficultyLevel} onValueChange={(value) => setFormData(prev => ({ ...prev, difficultyLevel: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DIFFICULTY_LEVELS.map(level => (
                    <SelectItem key={level.value} value={level.value}>
                      {level.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Exam Types - Multiple Selection */}
            <div className="space-y-2">
              <Label>Exam Types</Label>
              <p className="text-sm text-muted-foreground">
                Select exam types this question is suitable for (multiple selection allowed).
              </p>
              <TypeAheadSelect
                value=""
                onValueChange={(value) => {
                  if (value && !formData.examTypes.includes(value)) {
                    setFormData(prev => ({
                      ...prev,
                      examTypes: [...prev.examTypes, value]
                    }));
                  }
                }}
                placeholder="Select or add exam type..."
                options={examTypeOptions}
                onSearch={searchExamTypes}
                loading={examTypeLoading}
                allowCreate={true}
                onCreateNew={(value) => {
                  if (value && !formData.examTypes.includes(value)) {
                    setFormData(prev => ({
                      ...prev,
                      examTypes: [...prev.examTypes, value]
                    }));
                  }
                }}
              />

              {/* Selected Exam Types */}
              <div className="flex flex-wrap gap-2 min-h-[40px] p-3 border rounded-lg bg-muted/50">
                {formData.examTypes.length > 0 ? (
                  formData.examTypes.map((examType, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800"
                    >
                      {examType}
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            examTypes: prev.examTypes.filter(et => et !== examType)
                          }));
                        }}
                        className="ml-2 text-green-600 hover:text-green-800"
                      >
                        ×
                      </button>
                    </span>
                  ))
                ) : (
                  <span className="text-muted-foreground text-sm">No exam types selected</span>
                )}
              </div>
            </div>

            {/* Tags - Primary Categorization Method */}
            <div className="space-y-2">
              <Label>Tags *</Label>
              <p className="text-sm text-muted-foreground">
                Add tags to categorize this question. Tags determine which kits this question belongs to.
              </p>

              {/* Current Tags */}
              <div className="flex flex-wrap gap-2 min-h-[40px] p-3 border rounded-lg bg-muted/50">
                {formData.tags.length > 0 ? (
                  formData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        ×
                      </button>
                    </span>
                  ))
                ) : (
                  <span className="text-muted-foreground text-sm">No tags added yet</span>
                )}
              </div>

              {/* Add Tag Input */}
              <div className="flex gap-2">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Enter a tag (e.g., mathematics, algebra, fundamentals)"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                />
                <Button
                  type="button"
                  onClick={addTag}
                  disabled={!newTag.trim()}
                  variant="outline"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {/* Suggested Tags */}
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">Suggested tags:</p>
                <div className="flex flex-wrap gap-1">
                  {[
                    formData.subject?.toLowerCase(),
                    formData.topic?.toLowerCase(),
                    formData.difficultyLevel?.toLowerCase(),
                    'practice',
                    '2024'
                  ].filter(Boolean).filter(tag => !formData.tags.includes(tag!)).map((tag, index) => (
                    <Button
                      key={index}
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (tag && !formData.tags.includes(tag)) {
                          setFormData(prev => ({
                            ...prev,
                            tags: [...prev.tags, tag]
                          }));
                        }
                      }}
                      className="h-6 px-2 text-xs"
                    >
                      + {tag}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Tenant Access */}
          <div className="space-y-2">
            <Label>Access Scope</Label>
            <Input value="Current admin tenant only" disabled />
            <p className="text-xs text-muted-foreground">
              Admin questions remain tenant-specific. Shared super-admin questions are visible in the list but cannot be edited here.
            </p>
          </div>

          {/* Explanation */}
          <div className="space-y-2">
            <Label htmlFor="explanation">Explanation (Optional)</Label>
            <Textarea
              id="explanation"
              value={formData.explanation}
              onChange={(e) => setFormData(prev => ({ ...prev, explanation: e.target.value }))}
              placeholder="Enter explanation for the answer (LaTeX supported)..."
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={loading}>
              {loading ? 'Updating...' : 'Update Question'}
            </Button>
          </div>
          </div>

          {/* Preview Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Live Preview</h3>
            <KatexPreviewPanel
              questionText={formData.questionText}
              options={(formData.type === 'NUMERICAL' || formData.type === 'FILL_IN_THE_BLANK' || formData.type === 'FILL_IN_THE_BLANK_NUMERIC') ? [] : formData.options}
              explanation={formData.explanation}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
